import * as React from 'react';
import { gql, useApolloClient } from '@apollo/client';
import { MediaLibraryController, MediaLibraryControllerProps } from './MediaLibrary.controller';
import { useCallback, useContext, useState } from 'react';
import { AppContext } from '../../../providers/AppProvider';
import {
  MediaFilterInput,
  MediaOrigin,
  MediaProportions,
  MediaTypeFilter,
  useBulkArchiveMediaMutation,
  useGetMediaLibraryLazyQuery
} from '../../../models/graphql';
import { MaxLimits } from '../../../constants/media';
import { MediaLibraryFilterType } from '../../../models/media';
import { PostEditorContext } from '../../PostEditor/states/PostEditorProvider';

gql`
  query GetMediaLibrary($workspaceId: ID!, $input: MediaLibraryInput) {
    workspace(workspaceId: $workspaceId) {
      id
      name
      mediaLibrary(input: $input) {
        items {
          ...CoreMediaLibraryItemFields
          ... on Photo {
            ...CoreMediaLibraryPhotoFields
          }
          ... on Video {
            ...CoreMediaLibraryVideoFields
          }
        }
        cursor
        count
      }
    }
  }

  mutation BulkArchiveMedia($media: [MediaSelectionInput!]!) {
    bulkArchiveMedia(media: $media)
  }
`;

export type MediaLibraryApolloProps = {
  onCloseHandler?: MediaLibraryControllerProps['onCloseHandler'];
};

export const MediaLibraryApollo = (props: MediaLibraryApolloProps): JSX.Element => {
  const { cache } = useApolloClient();
  const [loadingMore, setLoadingMore] = useState<boolean>(false);
  const { selectedWorkspaceId } = useContext(AppContext);
  const { initialized: isInPostEditor } = useContext(PostEditorContext);

  const [getMediaLibrary, mediaLibraryResults] = useGetMediaLibraryLazyQuery({
    variables: { workspaceId: selectedWorkspaceId as string }
  });
  const [bulkArchiveMedia] = useBulkArchiveMediaMutation();

  const {
    fetchMore: mediaLibraryResultsFetchMore,
    data: mediaLibraryResultsData,
    variables: mediaLibraryResultsVariables
  } = mediaLibraryResults;

  const fetchInitialMediaItems = useCallback(
    (filters: MediaLibraryFilterType) => {
      const typeFilter = (filters.type as MediaTypeFilter) || undefined;
      const originFilter = (filters.origin as MediaOrigin) || undefined;
      const proportionsFilter = (filters.proportion as MediaProportions) || undefined;

      // MediaItemsPagination
      cache.modify({
        id: cache.identify({ id: selectedWorkspaceId, __typename: 'Workspace' }),
        fields: {
          mediaLibrary() {
            return undefined; // Or set to undefined if we want active queries to refetch
          }
        },
        broadcast: false
      });

      if (selectedWorkspaceId) {
        const filtersInput: MediaFilterInput = {
          type: typeFilter,
          origin: originFilter,
          proportions: proportionsFilter
        };

        getMediaLibrary({
          variables: {
            // IMPORTANT!: We use the context as a non-existent variable to differentiate cache objects. between the main ML page and the Post Editor Modal
            // TS will complain because it's not defined in the type, so we have to ignore
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            context: isInPostEditor ? 'posteditor' : 'medialibrary', // TODO make this variable based on true context
            workspaceId: selectedWorkspaceId,
            input: {
              limit: MaxLimits.QUERY,
              filters: filtersInput
            }
          }
        });
      }
    },
    [cache, getMediaLibrary, isInPostEditor, selectedWorkspaceId]
  );

  const fetchMoreMediaItems = useCallback(() => {
    if (selectedWorkspaceId) {
      if (!mediaLibraryResultsData || !mediaLibraryResultsVariables) {
        // We shouldn't be paginating if we have no results. Throw error instead?
        return;
      }

      setLoadingMore(() => true);

      const cursor = mediaLibraryResultsData.workspace.mediaLibrary.cursor;
      mediaLibraryResultsFetchMore({
        variables: {
          ...mediaLibraryResultsVariables,
          input: {
            ...mediaLibraryResultsVariables.input,
            filters: mediaLibraryResultsVariables?.input?.filters,
            cursor
          }
        }
      }).finally(() => {
        setLoadingMore(() => false);
      });
    }
  }, [mediaLibraryResultsData, mediaLibraryResultsFetchMore, mediaLibraryResultsVariables, selectedWorkspaceId]);

  const controllerProps: MediaLibraryControllerProps = {
    ...props,
    loadingMore,
    getMediaLibrary,
    mediaLibraryResults,
    bulkArchiveMedia,
    fetchInitialMediaItems,
    fetchMoreMediaItems
  };

  return <MediaLibraryController {...controllerProps} />;
};
